import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'

import { assignmentsApi } from '@/services/assignments'
import { jobsApi } from '@/services/jobs'
import { workersApi, type WorkerCreationPost } from '@/services/workers'
import { catalogRolesApi } from '@/services/catalog-roles'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/common/searchable-select'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { getErrorMessage } from '@/lib/api'
import { unmaskMaskedId } from '@/lib/backend-ids'
import {
  ASSIGNMENT_STATUSES,
  ASSIGNMENT_STATUS_LABELS,
  resolveAssignmentStatus,
  type AssignmentStatus,
} from '@/lib/assignment-status'
import { formatJobTitleWithId } from '@/components/jobs/job-title-link'
import {
  resolveActiveServiceInstanceId,
  withServiceInstanceId,
} from '@/lib/service-instance'
import { resolveReturnToPath } from '@/lib/return-navigation'
import { resolveEventFamilyAssignmentsBasePath } from '@/lib/event-family-assignments-route'
import { useAuthStore } from '@/stores/auth-store'

const toOptionalNumber = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const normalizeComparableId = (value: unknown) => {
  const raw = String(value ?? '').trim()
  if (!raw) {
    return null
  }

  const unmasked = unmaskMaskedId(value)
  if (unmasked != null) {
    return String(unmasked)
  }

  return raw
}

const getPostReferenceId = (post: WorkerCreationPost) => {
  const candidates = [post.post_id, post.id, post._id]
  const matched = candidates.find((candidate) => String(candidate ?? '').trim().length > 0)
  return matched != null ? String(matched) : ''
}

const postIdsMatch = (post: WorkerCreationPost, targetPostId: string | number) => {
  const normalizedTarget = normalizeComparableId(targetPostId)
  if (!normalizedTarget) {
    return false
  }

  const candidates = [post._id, post.post_id, post.id]
  return candidates.some((candidate) => normalizeComparableId(candidate) === normalizedTarget)
}

const resolveServiceInstanceIdFromRecord = (value: unknown) => {
  if (!value || typeof value !== 'object') {
    return null
  }

  const record = value as Record<string, unknown>
  const serviceInstanceInfo = record.service_instance_info as Record<string, unknown> | undefined
  const branchInfo = record.branch_info as Record<string, unknown> | undefined
  const candidates = [
    record.service_instance_id,
    record.service_instance,
    record.owner_service_instance,
    record.owner_service_instance_id,
    record.origin_service_instance,
    record.origin_service_instance_id,
    record.branch,
    record.branch_id,
    serviceInstanceInfo?.instance_id,
    serviceInstanceInfo?.id,
    serviceInstanceInfo?._id,
    branchInfo?.instance_id,
    branchInfo?.id,
    branchInfo?._id,
  ]

  for (const candidate of candidates) {
    const normalized = String(candidate ?? '').trim()
    if (normalized) {
      return normalized
    }
  }

  return null
}

export default function EventAssignmentFormPage() {
  const { id } = useParams<{ id: string }>()
  const isEditMode = Boolean(id)
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currentPost = useAuthStore((state) => state.currentPost)
  const currentBranch = useAuthStore((state) => state.currentBranch)
  const availableBranches = useAuthStore((state) => state.availableBranches)
  const authServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const accessToken = useAuthStore((state) => state.accessToken)
  const effectiveServiceInstanceId = useMemo(
    () => resolveActiveServiceInstanceId({
      search: location.search,
      post: currentPost,
      currentBranch,
      availableBranches,
      storedServiceInstanceId: authServiceInstanceId,
      token: accessToken,
    }),
    [
      accessToken,
      authServiceInstanceId,
      availableBranches,
      currentBranch,
      currentPost,
      location.search,
    ],
  )
  const searchParams = new URLSearchParams(location.search)

  const prefilledEventId = searchParams.get('job') || searchParams.get('job_id') || ''
  const assignmentsBasePath = useMemo(
    () => resolveEventFamilyAssignmentsBasePath(location.pathname),
    [location.pathname],
  )
  const currentServiceVariant = String(
    currentPost?.service_variant_unique_id
    ?? currentPost?.service_variant_name
    ?? currentPost?.service_variant_type
    ?? '',
  ).trim()

  const [selectedEventId, setSelectedEventId] = useState(prefilledEventId)
  const [selectedPostId, setSelectedPostId] = useState('')
  const [selectedRoleId, setSelectedRoleId] = useState('')
  const [status, setStatus] = useState<AssignmentStatus>('pending')

  const assignmentTargetsQuery = useQuery({
    queryKey: ['event-assignment-targets', effectiveServiceInstanceId ?? 'none'],
    queryFn: async () => {
      const [events, committees, tasks] = await Promise.all([
        jobsApi.getEvents({ page_size: 200, service_instance_id: effectiveServiceInstanceId ?? undefined }),
        jobsApi.getCommittees({ page_size: 200, service_instance_id: effectiveServiceInstanceId ?? undefined }),
        jobsApi.getTasks({ page_size: 200, service_instance_id: effectiveServiceInstanceId ?? undefined }),
      ])
      const merged = [...(events.results ?? []), ...(committees.results ?? []), ...(tasks.results ?? [])]
      const deduped = merged.filter((item, index, array) => index === array.findIndex((entry) => String(entry.id) === String(item.id)))
      return deduped
    },
    staleTime: 30_000,
  })

  const selectedTargetQuery = useQuery({
    queryKey: ['event-assignment-target', selectedEventId || 'none', effectiveServiceInstanceId ?? 'none'],
    queryFn: () => jobsApi.getJob(selectedEventId, { service_instance_id: effectiveServiceInstanceId ?? undefined }),
    enabled: Boolean(selectedEventId),
    staleTime: 30_000,
  })

  const selectedTargetServiceInstanceId = useMemo(() => {
    const fromTargetQuery = resolveServiceInstanceIdFromRecord(selectedTargetQuery.data)
    if (fromTargetQuery) {
      return fromTargetQuery
    }

    const selectedFromTargets = (assignmentTargetsQuery.data ?? []).find(
      (target: any) => String(target.id) === String(selectedEventId),
    )
    return resolveServiceInstanceIdFromRecord(selectedFromTargets)
  }, [assignmentTargetsQuery.data, selectedEventId, selectedTargetQuery.data])

  const resolvedServiceInstanceId = effectiveServiceInstanceId ?? selectedTargetServiceInstanceId

  const postsQuery = useQuery({
    queryKey: ['event-assignment-posts', resolvedServiceInstanceId ?? 'none'],
    queryFn: () => workersApi.getWorkerCreationPosts({
      serviceInstanceId: resolvedServiceInstanceId!,
      eligibleOnly: false,
    }),
    enabled: Boolean(resolvedServiceInstanceId),
    staleTime: 30_000,
  })

  const assignmentQuery = useQuery({
    queryKey: ['event-assignment', id],
    queryFn: () => assignmentsApi.getAssignment(id as string),
    enabled: isEditMode,
    staleTime: 30_000,
  })

  const roleOptionsQuery = useQuery({
    queryKey: ['event-assignment-roles', resolvedServiceInstanceId ?? 'none', currentServiceVariant || 'none'],
    queryFn: () => catalogRolesApi.list({
      serviceInstanceId: resolvedServiceInstanceId ?? undefined,
      serviceVariant: currentServiceVariant || undefined,
      includeHidden: false,
      kind: 'event',
    }),
    enabled: Boolean(resolvedServiceInstanceId),
    staleTime: 60_000,
  })

  const workerSkillDetailQuery = useQuery({
    queryKey: ['event-assignment-worker-skill-detail', assignmentQuery.data?.worker_skill],
    queryFn: () => workersApi.getWorkerSkill(String(assignmentQuery.data?.worker_skill)),
    enabled: isEditMode && Boolean(assignmentQuery.data?.worker_skill),
    staleTime: 60_000,
  })

  const selectedEvent = useMemo(() => {
    const events = assignmentTargetsQuery.data ?? []
    return events.find((event: any) => String(event.id) === String(selectedEventId))
  }, [assignmentTargetsQuery.data, selectedEventId])

  const eventOptions = useMemo(
    () => {
      const options = (assignmentTargetsQuery.data ?? []).map((event: any) => ({
        value: String(event.id),
        label: formatJobTitleWithId({ jobId: event.id, title: event.title, jobRef: event.job_ref, entityLabel: 'Event' }),
        keywords: `${event.title || ''} ${event.job_ref || ''} ${event.id || ''} ${event.kind_name || ''}`.trim(),
      }))

      const selectedInOptions = selectedEventId && options.some((entry) => entry.value === String(selectedEventId))
      if (!selectedInOptions && selectedTargetQuery.data) {
        options.unshift({
          value: String(selectedTargetQuery.data.id),
          label: formatJobTitleWithId({
            jobId: selectedTargetQuery.data.id,
            title: selectedTargetQuery.data.title,
            jobRef: selectedTargetQuery.data.job_ref,
            entityLabel: 'Event',
          }),
          keywords: `${selectedTargetQuery.data.title || ''} ${selectedTargetQuery.data.job_ref || ''} ${selectedTargetQuery.data.id || ''}`.trim(),
        })
      }

      return options
    },
    [assignmentTargetsQuery.data, selectedEventId, selectedTargetQuery.data],
  )

  const postOptions = useMemo(() => {
    const options = (postsQuery.data ?? []).map((post) => {
      const postReference = getPostReferenceId(post)
      const username = String(post.username || post.user_name || `Post #${postReference || 'Unknown'}`).trim()
      const roleName = String(post.role_name || '').trim()
      const label = roleName ? `${username} • ${roleName}` : username
      const keywords = `${username} ${roleName} ${post.post_id || ''} ${post.id || ''} ${post._id || ''}`.trim()
      return {
        value: postReference,
        label,
        keywords,
      }
    })

    if (isEditMode && assignmentQuery.data?.worker_id) {
      const existingWorkerId = String(assignmentQuery.data.worker_id)
      const hasMatch = (postsQuery.data ?? []).some((post) => String(post.worker_id ?? '') === existingWorkerId)
      if (!hasMatch) {
        const fallbackLabel = String(assignmentQuery.data.worker_name || '').trim() || `Worker #${existingWorkerId}`
        options.unshift({
          value: '',
          label: `${fallbackLabel} (select post)`,
          keywords: fallbackLabel,
        })
      }
    }

    return options
  }, [assignmentQuery.data, isEditMode, postsQuery.data])

  const roleOptions = useMemo(
    () => (roleOptionsQuery.data ?? [])
      .filter((role) => {
        if (!currentServiceVariant) {
          return true
        }

        const expected = currentServiceVariant.toLowerCase()
        const roleVariantId = String(role.service_variant ?? '').trim().toLowerCase()
        const roleVariantName = String(role.service_variant_name ?? '').trim().toLowerCase()
        return roleVariantId === expected || roleVariantName === expected
      })
      .map((role) => ({
        value: String(role.id),
        label: String(role.name || role.title || '').trim() || `Role #${role.id}`,
        keywords: `${role.name || ''} ${role.title || ''} ${role.code || ''} ${role.unique_id || ''}`.trim(),
      })),
    [roleOptionsQuery.data, currentServiceVariant],
  )

  const ensureWorkerForPost = async (postId: string) => {
    const postList = await workersApi.getWorkerCreationPosts({
      serviceInstanceId: resolvedServiceInstanceId,
      eligibleOnly: false,
    })
    const selected = postList.find((entry) => postIdsMatch(entry, postId))
    if (!selected) {
      throw new Error('Selected post not found for this service instance')
    }

    const serviceInstanceRef = resolvedServiceInstanceId
      ?? resolveServiceInstanceIdFromRecord(selected)
    if (!serviceInstanceRef) {
      throw new Error('Service instance is required')
    }

    if (selected.worker_id != null) {
      return String(selected.worker_id)
    }

    const displayName = String(selected.username || selected.user_name || selected.role_name || `Post ${postId}`).trim()

    try {
      const created = await workersApi.createWorker({
        post_id: getPostReferenceId(selected),
        display_name: displayName,
        service_instance: serviceInstanceRef,
        is_active: true,
      })
      return String(created.id)
    } catch (_error) {
      const refreshed = await workersApi.getWorkerCreationPosts({
        serviceInstanceId: serviceInstanceRef,
        eligibleOnly: false,
      })
      const resolved = refreshed.find((entry) => postIdsMatch(entry, postId))
      if (resolved?.worker_id != null) {
        return String(resolved.worker_id)
      }
      throw new Error('Unable to resolve worker profile for selected post')
    }
  }

  const buildReturnPath = (eventId?: string | number | null) => {
    const params = new URLSearchParams()
    const resolvedEventId = String(eventId ?? selectedEventId ?? '').trim()
    if (resolvedEventId) {
      params.set('job_id', resolvedEventId)
    }

    return withServiceInstanceId(
      `${assignmentsBasePath}${params.toString() ? `?${params.toString()}` : ''}`,
      resolvedServiceInstanceId ?? effectiveServiceInstanceId,
    )
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedEventId) {
        throw new Error('Event is required')
      }

      if (!selectedPostId) {
        throw new Error('Please select a post')
      }

      const workerId = await ensureWorkerForPost(selectedPostId)

      if (!workerId) {
        throw new Error('Unable to resolve worker profile for selected post')
      }

      if (!selectedRoleId) {
        throw new Error('Please select an event role')
      }

      if (!roleOptions.some((role) => role.value === selectedRoleId)) {
        throw new Error('Selected role is not allowed for Event assignments.')
      }

      const eventCategoryId = selectedEvent?.category
        ?? (selectedEvent as { category_id?: string | number | null } | undefined)?.category_id

      const workerSkills = await workersApi.getWorkerSkills({
        worker: workerId,
        skill_role: selectedRoleId,
        is_active: true,
        page_size: 50,
        job_category: eventCategoryId != null && String(eventCategoryId).trim() ? String(eventCategoryId) : undefined,
        service_instance_id: resolvedServiceInstanceId ?? undefined,
      })

      let workerSkillId = workerSkills[0]?.id
      if (!workerSkillId) {
        const createdWorkerSkill = await workersApi.createWorkerSkill({
          worker: workerId,
          skill_role: selectedRoleId,
          job_category: eventCategoryId != null && String(eventCategoryId).trim() ? String(eventCategoryId) : undefined,
          is_active: true,
        })
        workerSkillId = createdWorkerSkill.id
      }

      const payload: Record<string, unknown> = {
        job: selectedEventId,
        worker: workerId,
        worker_skill: workerSkillId,
        status,
        pay_type: 'per_hour',
        pay: 0,
        night_pay: 0,
        holiday_pay: 0,
        peak_pay: 0,
        pay_flat: 0,
        estimated_duration: toOptionalNumber(selectedEvent?.scheduled_hours ?? selectedEvent?.estimated_hours) ?? 0,
      }

      if (isEditMode) {
        return assignmentsApi.updateAssignment(id as string, payload)
      }

      return assignmentsApi.createAssignment(payload)
    },
    onSuccess: (assignment) => {
      toast.success(isEditMode ? 'Event assignment updated successfully' : 'Event assignment created successfully')
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
      queryClient.invalidateQueries({ queryKey: ['job-assignments', assignment.job_id] })
      queryClient.invalidateQueries({ queryKey: ['worker-assignments'] })
      queryClient.invalidateQueries({ queryKey: ['event-assignment-workers'] })
      navigate(resolveReturnToPath(location.search, buildReturnPath(assignment.job_id)))
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  useEffect(() => {
    if (!isEditMode || !assignmentQuery.data) {
      return
    }

    const assignment = assignmentQuery.data
    setSelectedEventId(String(assignment.job_id || prefilledEventId || ''))
    if (assignment.worker_id != null) {
      const workerId = String(assignment.worker_id)
      const matchedPost = (postsQuery.data ?? []).find((post) => String(post.worker_id ?? '') === workerId)
      setSelectedPostId(matchedPost ? getPostReferenceId(matchedPost) : '')
    }
    setStatus(resolveAssignmentStatus(assignment.status) ?? 'pending')
  }, [assignmentQuery.data, isEditMode, postsQuery.data, prefilledEventId])

  useEffect(() => {
    if (!isEditMode || selectedRoleId) {
      return
    }

    const roleId = workerSkillDetailQuery.data?.skill_role
    if (roleId != null && String(roleId).trim()) {
      setSelectedRoleId(String(roleId))
    }
  }, [isEditMode, selectedRoleId, workerSkillDetailQuery.data?.skill_role])

  const isLoading = saveMutation.isPending || (isEditMode && assignmentQuery.isLoading)

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(buildReturnPath())}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-foreground">{isEditMode ? 'Edit Event Assignment' : 'Add Event Assignment'}</h1>
            <p className="text-xs text-muted-foreground">Choose an existing worker or add a new user as worker automatically.</p>
          </div>
        </div>
        <Button variant="outline" asChild>
          <Link to={buildReturnPath()}>Back to Assignments</Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Assignment Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="event">Event / Committee / Task</Label>
            <SearchableSelect
              id="event"
              ariaLabel="Event, Committee, or Task"
              value={selectedEventId}
              onValueChange={setSelectedEventId}
              options={eventOptions}
              placeholder={assignmentTargetsQuery.isLoading ? 'Loading items...' : 'Select event, committee, or task'}
              searchPlaceholder="Search events, committees, or tasks"
              emptyText={assignmentTargetsQuery.isLoading ? 'Loading items...' : 'No items found'}
              disabled={assignmentTargetsQuery.isLoading}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="post">Post</Label>
            <SearchableSelect
              id="post"
              ariaLabel="Post"
              value={selectedPostId}
              onValueChange={setSelectedPostId}
              options={postOptions}
              placeholder={
                !resolvedServiceInstanceId
                  ? 'Select an event or open a branch workspace first'
                  : postsQuery.isLoading
                    ? 'Loading posts...'
                    : 'Select post'
              }
              searchPlaceholder="Search by username or role"
              emptyText={
                !resolvedServiceInstanceId
                  ? 'Service instance is required before posts can load'
                  : postsQuery.isLoading
                    ? 'Loading posts...'
                    : postsQuery.isError
                      ? 'Unable to load posts'
                      : 'No posts found'
              }
              disabled={!resolvedServiceInstanceId || postsQuery.isLoading}
            />
            <p className="text-xs text-muted-foreground">
              {!resolvedServiceInstanceId
                ? 'Choose an event, committee, or task first so posts can load for that service instance.'
                : 'If no worker profile exists for this post, it will be created automatically.'}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="event_role">Event Role</Label>
            <SearchableSelect
              id="event_role"
              ariaLabel="Event Role"
              value={selectedRoleId}
              onValueChange={setSelectedRoleId}
              options={roleOptions}
              placeholder={roleOptionsQuery.isLoading ? 'Loading event roles...' : 'Select event role'}
              searchPlaceholder="Search event roles"
              emptyText={roleOptionsQuery.isLoading ? 'Loading event roles...' : 'No event roles found'}
              disabled={roleOptionsQuery.isLoading}
            />
          </div>

          {isEditMode ? (
            <div className="space-y-1.5">
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={(value: AssignmentStatus) => setStatus(value)}>
                <SelectTrigger id="status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {ASSIGNMENT_STATUSES.map((statusOption) => (
                    <SelectItem key={statusOption} value={statusOption}>
                      {ASSIGNMENT_STATUS_LABELS[statusOption]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate(buildReturnPath())}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={isLoading || !selectedEventId || !selectedRoleId || !selectedPostId}
              onClick={() => saveMutation.mutate()}
            >
              {isLoading ? (
                <>
                  <Spinner className="mr-2" />
                  {isEditMode ? 'Saving...' : 'Creating...'}
                </>
              ) : isEditMode ? 'Save Event Assignment' : 'Add Assignment'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
